import { ArrowUpRight, ExternalLink, PenLine, Newspaper, Radio } from "lucide-react";
import { EDITORIAL_FEATURES, SYNDICATED_COVERAGE, THOUGHT_LEADERSHIP } from "@/lib/mediaCoverage";
import { ArticlePreviews } from "@/components/ArticlePreviews";

const ext = { target: "_blank", rel: "noopener noreferrer" };

const Heading = ({ icon: Icon, eyebrow, title, intro }) => (
  <>
    <div className="flex items-center gap-3 mb-2">
      <Icon size={16} style={{ color: "var(--gold)" }} />
      <p className="eyebrow" style={{ color: "var(--gold)" }}>{eyebrow}</p>
    </div>
    <h2 className="font-serif font-light tracking-tighter text-3xl sm:text-4xl lg:text-5xl leading-[0.95] mb-4">
      {title}
    </h2>
    <p className="text-white/60 text-sm sm:text-base max-w-2xl leading-relaxed mb-10">{intro}</p>
  </>
);

const EditorialFeatures = () => (
  <section className="max-w-[1200px] mx-auto px-6 lg:px-12 pt-16" data-testid="press-editorial">
    <Heading
      icon={Newspaper}
      eyebrow="Editorial Features"
      title="Interviews & profiles."
      intro="Original editorial coverage of Jamilah N. Lawry (professionally known as Nina Capone) — interviews and leadership profiles written by independent publications."
    />
    <div className="grid md:grid-cols-2 gap-5">
      {EDITORIAL_FEATURES.map((f) => (
        <a
          key={f.id}
          href={f.url}
          {...ext}
          className="surface p-7 lg:p-8 flex flex-col gap-4 group hover:border-[color:var(--gold)]/40 transition-colors"
          data-testid={`editorial-${f.id}`}
        >
          <div className="flex flex-wrap items-center justify-between gap-3">
            <span className="font-serif text-2xl tracking-tight whitespace-nowrap text-white group-hover:text-[color:var(--gold)] transition-colors">
              {f.outlet}
            </span>
            <span className="text-[9px] uppercase tracking-[0.18em] px-2 py-1 rounded-full border border-[color:var(--gold)]/30 text-[color:var(--gold)] shrink-0">
              {f.kind}
            </span>
          </div>
          <p className="text-white/75 leading-relaxed">&ldquo;{f.title}&rdquo;</p>
          <span className="text-xs link-gold inline-flex items-center gap-1.5 mt-auto">
            Read the feature <ArrowUpRight size={13} />
          </span>
        </a>
      ))}
    </div>
  </section>
);

const SyndicatedCoverage = () => (
  <section className="max-w-[1200px] mx-auto px-6 lg:px-12 pt-16" data-testid="press-syndicated">
    <Heading
      icon={Radio}
      eyebrow="Syndicated Coverage"
      title="Distributed across the wire."
      intro="News-release coverage of Jamilah N. Lawry (professionally known as Nina Capone), syndicated through EIN Presswire and picked up by partner outlets."
    />
    <div className="divide-y divide-white/5 border-y border-white/5">
      {SYNDICATED_COVERAGE.map((s) => (
        <a
          key={s.id}
          href={s.url}
          {...ext}
          className="py-5 grid sm:grid-cols-[220px_1fr_auto] gap-2 sm:gap-6 items-baseline group"
          data-testid={`syndicated-${s.id}`}
        >
          <div className="min-w-0">
            <p className="text-white font-medium group-hover:text-[color:var(--gold)] transition-colors">{s.outlet}</p>
            <p className="text-[11px] uppercase tracking-[0.18em] text-white/40 mt-1">{s.date}</p>
          </div>
          <p className="text-sm text-white/60 leading-relaxed min-w-0 break-words">{s.title}</p>
          <ExternalLink size={14} className="hidden sm:block text-white/35 group-hover:text-[color:var(--gold)] transition-colors" />
        </a>
      ))}
    </div>
  </section>
);

const ThoughtLeadership = () => (
  <section className="max-w-[1200px] mx-auto px-6 lg:px-12 pt-16" data-testid="press-thought-leadership">
    <Heading
      icon={PenLine}
      eyebrow="Published Thought Leadership"
      title="In her own words."
      intro="Authored content — articles written by Jamilah N. Lawry (professionally known as Nina Capone), not third-party reporting."
    />
    <a
      href={THOUGHT_LEADERSHIP.url}
      {...ext}
      className="surface p-7 lg:p-8 flex flex-col sm:flex-row sm:items-center gap-5 sm:justify-between group hover:border-[color:var(--gold)]/40 transition-colors"
      data-testid="thought-leadership-link"
    >
      <div className="space-y-3 min-w-0">
        <span className="inline-block text-[9px] uppercase tracking-[0.18em] px-2 py-1 rounded-full border border-white/15 text-white/60" data-testid="thought-leadership-label">
          Authored Content
        </span>
        <p className="font-serif text-2xl tracking-tight group-hover:text-[color:var(--gold)] transition-colors">
          {THOUGHT_LEADERSHIP.title}
        </p>
        <p className="text-sm text-white/55">
          {THOUGHT_LEADERSHIP.outlet} · {THOUGHT_LEADERSHIP.topics.join(" · ")}
        </p>
      </div>
      <span className="btn-ghost-gold inline-flex items-center gap-2 px-6 py-3 text-xs shrink-0 self-start sm:self-auto">
        Read her articles <ArrowUpRight size={14} />
      </span>
    </a>
    <ArticlePreviews />
  </section>
);

export const PressCoverage = () => (
  <>
    <EditorialFeatures />
    <SyndicatedCoverage />
    <ThoughtLeadership />
  </>
);
